import React, { useEffect, useState, useCallback } from 'react';
import { UserGuideStyle } from './UserGuide.style';
import UserGuideVideo from './UserGuideVideo';

const userGuideData = {
  mainTitle: 'User Guidelines',
  subtitle: 'Everything you need to know about managing your playlists with Songplace',
  platformGuide: {
    title: 'Platform User Guide',
    subtitle: 'Introduction',
    content:
      'Welcome to Songplace – Your Ultimate Playlist Management Platform.\n\nThis guide will walk you through the diverse features designed to enhance your music curation experience.',
  },
  sections: [
    {
      id: 'search',
      title: 'Search',
      number: 1,
      content:
        'Purpose: Find your favorite songs from the huge library of Spotify right through Songplace.\n\nFunction:\n\n1. Click on "Browse" option available in the left menu list.\n\n2. Type in any keyword in the search bar and hit Enter.',
      hasVideo: false,
    },
    {
      id: 'song-scheduling',
      title: 'Song Scheduling',
      number: 2,
      content:
        'Purpose: Dynamically manage your playlists by scheduling songs with specific start and end dates.\n\nFunction:\n\n1. Go to the "browse" feature available in the left menu list.\n\n2. Type any song keyword and click the "+" button on the right to access the song schedule modal.\n\n3. Select the desired playlist and position of the song along with a start or end date and a personalized note to schedule the song, organizing your music collection based on your preferred timeline.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/Songplace+Final+Final.mp4',
    },
    {
      id: 'pre-release',
      title: 'Pre-release',
      number: 3,
      content:
        'Purpose: Schedule unreleased songs to be added automatically to your playlist upon launch.\n\nFunction:\n\n1. Access the "pre-release" feature available in the left menu list, click "Add song" to access the song scheduling modal.\n\n2. Select the desired playlist and position of the song and then enter the dedicated URI of the unreleased song along with a start or end date, and a personalized note and watch it seamlessly integrate into your playlist upon release.\n\n3. In the meantime, you can check the list of scheduled tracks by simply accessing the pre-release feature.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/Pre+release.mp4',
    },
    {
      id: 'refresh-playlist',
      title: 'Refresh Playlist',
      number: 4,
      content:
        'Purpose: Quickly update all the scheduled tracks in your playlist to the most recent date and time.\n\nFunction:\n\n1. Go to any playlist and click the "Refresh playlist" button available in the header.\n\n2. The page will get reloaded and modify each song\'s preset start date and time to the current date and time according to your calendar.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/refresh+playlist.mp4',
    },
    {
      id: 'notes',
      title: 'Notes',
      number: 5,
      content:
        'Purpose: Personalize your playlist by adding notes or descriptions to each song.\n\nFunction:\n\n1. Open any playlist and click the three dots available at the right under the "actions" tab of each song to access the action features.\n\n2. Choose "Add note", a popup will appear, type in a personalized note associated with the selected song and click edit to add it. You can also delete/remove a note by simply clicking the "delete note" button available in the popup.\n\n3. The page will get reloaded and your note will be added to the specific song no matter how many copies it has in the playlist.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/add+note.mp4',
    },
    {
      id: 'delete',
      title: 'Delete',
      number: 6,
      content:
        'Purpose: Easily manage your playlists by removing songs you no longer want.\n\nFunction:\n\n1. Go to the action tab in any playlist to find the action features for any song.\n\n2. Choose "delete song from the playlist" and your song along with its copies will permanently be deleted from the particular playlist.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/delete+song.mp4',
    },
    {
      id: 'move',
      title: 'Move',
      number: 7,
      content:
        'Purpose: Move songs from one playlist to another to suit your preferred listening order.\n\nFunction:\n\n1. You can easily move any song from any playlist to one or more playlists. Access the action drop down for the song that you want to move.\n\n2. Click "Change playlist" option, a popup will appear.\n\n3. Choose one more playlist from the playlist dropdown and the desired position for the song. Your song will be removed from the current playlist and added to the desired ones.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/move+song.mp4',
    },
    {
      id: 'playlist-create-edit',
      title: 'Playlist Create/Edit',
      number: 8,
      content:
        'Purpose: Craft and refine your playlists according to your evolving musical preferences.\n\nFunction:\n\n1. You can create or edit unlimited playlists within the Songplace platform that is connected to your Spotify or Apple Music account. Click "Create playlist" option available on the left side of the dashboard, this will open the playlist creation modal.\n\n2. Type in the name and description for your playlist and click add. Once done, you need to refresh the page manually to align the playlist with both Songplace and Spotify.\n\n3. In order to edit the playlist, open the playlist and click "Edit playlist" option available in the playlist header.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/create+playlist.mp4',
    },
    {
      id: 'link-multiple-profiles',
      title: 'Link Multiple Profiles',
      number: 9,
      content:
        'Purpose: Sync and access your playlists across multiple profiles seamlessly.\n\nFunction:\n\n1. Connect multiple profiles to ensure consistent playlist access and updates. Go to "Account overview" in the left action tab available on left menu list or click 3 dots available right after your account name to access "Account overview".\n\n2. On the account overview screen, go to "Linked accounts", this will show you all the accounts/profiles connected to your Songplace account.\n\n3. To add another profile, click "Add account", this will take you to the Spotify connection screen.\n\n4. Log in to the account that you want to add or select "Agree" if you have already logged in.\n\n5. Once done, refresh the page to align both profiles with Songplace. You can easily switch between multiple profiles through account overview screen or the action dropdown available with your account name.',
      hasVideo: false,
    },
    {
      id: 'song-popularity',
      title: 'Song Popularity',
      number: 10,
      content:
        'Purpose: Stay updated on the popularity of your songs.\n\nFunction:\n\n1. To view any song\'s popularity index, click the 3 dots available for each song in the action tab.\n\n2. Choose "View details" from the action dropdown to view details along with the popularity index of that specific song.',
      hasVideo: false,
    },
    {
      id: 'priority-fixed-position',
      title: 'Priority/Reset Position',
      number: 11,
      content:
        'Purpose: Highlight your favorite songs by placing them in priority positions.\n\nFunction:\n\n1. Elevate songs to priority positions within your playlists, ensuring they get the attention they deserve. When you want to lock a song to a desired position, simply change the song\'s position to the desired one through "move song" feature available in the action dropdown. Ignore this step if the song is already on the desired position.\n\n2. Once done, go to the action dropdown of that song and click "Reset position", this will reload the page and give your song a unique lock identifier to ensure that your song has been locked to this position in both Songplace and Spotify until you unlock it or move to any other position.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/locked+position+on+1.mp4',
    },
    {
      id: 'fix-playlist',
      title: 'Fix Playlist',
      number: 12,
      content:
        'Purpose: Sync your playlist with the songs that have a priority position locked.\n\nFunction:\n\n1. This feature work as a side action for priority position. When you lock a song to a desired position, there is a chance that it might get moved to another position through Spotify. But, to revert this action, all you need to do is just clicking the "Sync Playlist" button available in the header of each playlist. This will sync your songs to their original priority positions.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/fixed+Playlist.mp4',
    },
    {
      id: 'description',
      title: 'Description',
      number: 13,
      content:
        'Purpose: Add descriptions to songs for a personalized music journey.\n\nFunction:\n\n1. Input details or mood descriptions to create a richer connection with each track. You can add a description to the song while adding the song to a playlist through the scheduling modal or using the action dropdown of the particular song and selecting "add notes".\n\n2. You can always read the notes by simply hovering to the notes icon available on the song.',
      hasVideo: false,
    },
    {
      id: 'preview-play',
      title: 'Preview Play',
      number: 14,
      content:
        'Purpose: Test songs before finalizing your playlist.\n\nFunction:\n\n1. Use the preview play feature to ensure each song fits seamlessly into your curated collection.\n\n2. The preview play button appears to each song (if available publicly) when you hover the song through either the browsing section, playlists or wherever the song is listed.',
      hasVideo: false,
    },
    {
      id: 'bulk-mode',
      title: 'Bulk Mode',
      number: 15,
      content:
        'Purpose: Add as many songs as you want in the playlist with just a single click.\n\nFunction:\n\n1. Unlike scheduling, when you just want to add multiple songs to a specific playlist easily and quickly you can do that by enabling the "Add to bulk" button available in each playlist. Go to any playlist and enable "Add to bulk" button then search any keyword through the search bar available in that playlist and click the "+" sign to add the song.\n\n2. You will see a popup confirming that your song has been added to the playlist. You can also pick any playlist after enabling the bulk mode but before running any query search. If you pick a different playlist after making the search the bulk mode will automatically be disabled.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/add+in+bulk.mp4',
    },
    {
      id: 'songplace-only-mode',
      title: 'Songplace Only Mode',
      number: 16,
      content:
        'Purpose: Enabling Songplace only mode is the best option when you only want to see the songs with songplace features applied.\n\nFunction:\n\n1. Songplace only button modifies your playlists with the songs that have been modified with Songplace features such as end dates, notes, and priority position. For doing that, go to any playlist from which you want to see the modified songs and enable the "songplace only" button available in the header.\n\n2. This will modify the playlist then and there with the songplace modified songs.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/songplace+only.mp4',
    },
    {
      id: 'schedule-refresh',
      title: 'Schedule Refresh',
      number: 17,
      content:
        'Purpose: Schedule the refresh playlist feature based on a custom duration so you don\'t have to manually refresh the playlist every time.\n\nFunction:\n\n1. You can set the scheduler by clicking the "Schedule refresh" button available in the header of each playlist (with at least 1 song). Once clicked, set a personalized duration like daily, weekly, or monthly with a start date and time accordingly.\n\n2. The date and time you select will trigger the scheduler to start and it will automatically perform the action thereafter unless stopped manually. However, in case of any unforeseen error the "Scheduler refresh" button will turn red and give you an option to reset the scheduler.',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/final-schedule-refresh.mp4',
    },
    {
      id: 'schedule-reset-position',
      title: 'Schedule Reset Position',
      number: 18,
      content:
        'Purpose: Schedule the reset playlist feature based on a custom duration so you don\'t have to manually reset the songs positions every time.\n\nFunction:\n\n1. You can set the scheduler by clicking the "Schedule reset position" button available in the header of each playlist (with at least 1 locked position available). Once clicked, set a personalized duration like daily, weekly, or monthly with a start date and time accordingly.\n\n2. The date and time you select will trigger the scheduler to start and it will automatically perform the action thereafter unless stopped manually. However, in case of any unforeseen error the "Scheduler reset position" button will turn red so you can set a new scheduler (if needed).',
      hasVideo: true,
      videoSrc:
        'https://songplace-logo.s3.amazonaws.com/songplace-logo/user-guide-videos/final-schedule-reset-position.mp4',
    },
  ],
  conclusion: {
    title: 'Conclusion',
    content:
      'Explore the full potential of Songplace and make your playlist management experience truly unique. For any additional assistance, refer to our support resources or contact our customer service team.',
  },
};

function UserGuide() {
  const [searchText, setSearchText] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [autoSuggestArr] = useState([
    'Search',
    'Song Scheduling',
    'Pre-release',
    'Refresh playlist',
    'Notes',
    'Delete',
    'Move',
    'Playlist create/edit',
    'Link multiple profiles',
    'Song popularity',
    'Priority/Reset position',
    'Fix Playlist',
    'Description',
    'Preview play',
    'Bulk mode',
    'Songplace only mode',
    'Schedule Refresh',
    'Schedule Reset Position',
  ]);

  const onSearch = useCallback(() => {
    const relevantKeyword = [
      ['Browse', 'Explore', 'Discover', 'Search'],
      ['Scheduling', 'Timetable', 'Calendar', 'Song Scheduling'],
      ['Early access', 'Preview', 'Advance', 'Pre-release'],
      ['Update', 'Renew', 'Revise', 'Refresh playlist'],
      ['Annotations', 'Comments', 'Remarks', 'Notes'],
      ['Remove', 'Erase', 'Eliminate', 'Delete'],
      ['Rearrange', 'Shift', 'Transfer', 'Move'],
      ['Compilation', 'Customize', 'Modify', 'Playlist create/edit'],
      ['Connect', 'Associate', 'Combine', 'Link multiple profiles'],
      ['Popularity trends', 'Hit songs', 'Trending tracks', 'Song popularity'],
      ['Priority/Reset position', 'Importance ranking', 'Fixed order', 'Top position'],
      ['Repair', 'Correct', 'Amend', 'Fix Playlist'],
      ['Details', 'Information', 'Explanation', 'Description'],
      ['Sample', 'Trial', 'Test', 'Preview play'],
      ['Batch', 'Mass', 'Group', 'Bulk mode'],
      ['Exclusive', 'Restricted', 'Limited access', 'Songplace only mode'],
      ['Schedule Refresh', 'Auto refresh', 'Scheduled refresh', 'Automatic playlist refresh'],
      [
        'Schedule Reset Position',
        'Auto reset position',
        'Scheduled reset position',
        'Automatic position reset',
      ],
    ];

    const relevantArray = relevantKeyword.filter(
      arr => arr.filter(val => searchText.toLowerCase() === val.toLowerCase())[0]
    );

    let key = relevantArray.length
      ? relevantArray[0].filter(
          val => autoSuggestArr.filter(obj => val.toLowerCase() === obj.toLowerCase())[0]
        )
      : '';

    const scrollToElement = (element: HTMLElement) => {
      // Calculate header height to offset the scroll position
      const headerElement = document.querySelector('.userGuideHead') as HTMLElement;
      const headerHeight = headerElement ? headerElement.offsetHeight + 20 : 100; // 20px extra padding

      // Get the element's position relative to the scrollable container
      const mainContent = document.querySelector('.mainContent') as HTMLElement;
      if (mainContent) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = mainContent.getBoundingClientRect();
        const scrollTop = mainContent.scrollTop;
        const elementTop = elementRect.top - containerRect.top + scrollTop;

        // Scroll to the element with header offset
        mainContent.scrollTo({
          top: elementTop - headerHeight,
          behavior: 'smooth',
        });
      }
    };

    if (key.length) {
      const element = document.getElementById(key[0].toLowerCase().split(' ').join('-'));
      if (element) {
        scrollToElement(element);
      }
    } else {
      const matchedKey = autoSuggestArr.filter(
        val =>
          searchText.toLowerCase().split(' ').includes(val.toLowerCase()) ||
          searchText.toLowerCase().includes(val.toLowerCase())
      );
      if (matchedKey.length) {
        const element = document.getElementById(matchedKey[0].toLowerCase().split(' ').join('-'));
        if (element) {
          scrollToElement(element);
        }
      }
    }
  }, [searchText, autoSuggestArr]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Calculate header height to offset the scroll position
      const headerElement = document.querySelector('.userGuideHead') as HTMLElement;
      const headerHeight = headerElement ? headerElement.offsetHeight + 20 : 100; // 20px extra padding

      // Get the element's position relative to the scrollable container
      const mainContent = document.querySelector('.mainContent') as HTMLElement;
      if (mainContent) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = mainContent.getBoundingClientRect();
        const scrollTop = mainContent.scrollTop;
        const elementTop = elementRect.top - containerRect.top + scrollTop;

        // Scroll to the element with header offset
        mainContent.scrollTo({
          top: elementTop - headerHeight,
          behavior: 'smooth',
        });
      }
      setActiveSection(sectionId);
    }
  };

  // Handle video controls
  useEffect(() => {
    const videoElements = document.getElementsByClassName('video-react-video');
    if (videoElements.length) {
      Array.from(videoElements).forEach(function (element) {
        if (element instanceof HTMLVideoElement) {
          element.controls = true;
        }
      });
    }
  }, []);

  // Handle hash-based scrolling to sections
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const elementId = hash.substring(1);
      const element = document.getElementById(elementId);
      if (element) {
        setTimeout(() => {
          // Calculate header height to offset the scroll position
          const headerElement = document.querySelector('.userGuideHead') as HTMLElement;
          const headerHeight = headerElement ? headerElement.offsetHeight + 20 : 100; // 20px extra padding

          // Get the element's position relative to the scrollable container
          const mainContent = document.querySelector('.mainContent') as HTMLElement;
          if (mainContent) {
            const elementRect = element.getBoundingClientRect();
            const containerRect = mainContent.getBoundingClientRect();
            const scrollTop = mainContent.scrollTop;
            const elementTop = elementRect.top - containerRect.top + scrollTop;

            // Scroll to the element with header offset
            mainContent.scrollTo({
              top: elementTop - headerHeight,
              behavior: 'smooth',
            });
          }
          setActiveSection(elementId);
        }, 100);
      }
    }
  }, []);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = userGuideData.sections;
      let currentSection = '';

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            currentSection = section.id;
          }
        }
      }

      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);

  const renderContent = (content: string) => {
    const blocks = content.split('\n\n');
    return blocks.map((paragraph, idx) => {
      const lines = paragraph.split('\n');
      const steps = lines.filter(line => line.match(/^\d+\./));
      const nonSteps = lines.filter(line => !line.match(/^\d+\./));

      return (
        <div key={idx} className="contentBlock">
          {nonSteps.map((line, lineIdx) => {
            if (line.startsWith('Purpose:')) {
              const purposeText = line.replace('Purpose:', '').trim();
              return (
                <React.Fragment key={lineIdx}>
                  <h4 className="contentHeading">Purpose</h4>
                  {purposeText && <p className="contentParagraph">{purposeText}</p>}
                </React.Fragment>
              );
            } else if (line.startsWith('Function:')) {
              const functionText = line.replace('Function:', '').trim();
              return (
                <React.Fragment key={lineIdx}>
                  <h4 className="contentHeading">How it works</h4>
                  {functionText && <p className="contentParagraph">{functionText}</p>}
                </React.Fragment>
              );
            } else if (line.trim()) {
              return (
                <p key={lineIdx} className="contentParagraph">
                  {line.trim()}
                </p>
              );
            }
            return null;
          })}
          {steps.length > 0 && (
            <div className="stepsList">
              {steps.map((step, stepIdx) => (
                <div key={stepIdx} className="stepParagraph">
                  <span className="stepNumber">{step.split('.')[0]}</span>
                  <span className="stepText">{step.substring(step.indexOf('.') + 2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <UserGuideStyle>
      <div className="userGuideInner">
        {/* Header */}
        <div className="userGuideHead">
          <div className="headContent">
            <h1 className="userGuideTitle">{userGuideData.mainTitle}</h1>
            <p className="userGuideSubtitle">{userGuideData.subtitle}</p>
          </div>
          <div className="searchWrapper">
            <label className="searchLabel">
              <div className="searchIconDiv">
                <svg
                  width="15"
                  height="17"
                  viewBox="0 0 15 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.0578 11.7133L14.3263 15.5487L13.7594 16.0359L10.482 12.2005C9.35407 13.0567 8.08445 13.4849 6.67309 13.4849C5.81682 13.4849 4.99748 13.318 4.21506 12.9844C3.43264 12.6508 2.75944 12.202 2.19547 11.638C1.6315 11.0741 1.18271 10.4009 0.849091 9.61843C0.515476 8.83598 0.348656 8.01664 0.348633 7.16039C0.34861 6.30415 0.515429 5.48481 0.849091 4.70236C1.18275 3.91992 1.63155 3.24672 2.19547 2.68278C2.7594 2.11883 3.43259 1.67004 4.21506 1.3364C4.99753 1.00276 5.81687 0.835938 6.67309 0.835938C7.52931 0.835937 8.34866 1.00276 9.13112 1.3364C9.91359 1.67004 10.5868 2.11883 11.1507 2.68278C11.7146 3.24672 12.1634 3.91992 12.4971 4.70236C12.8308 5.48481 12.9976 6.30415 12.9975 7.16039C12.9975 8.04026 12.8263 8.8729 12.4838 9.65829C12.1413 10.4437 11.6659 11.1287 11.0577 11.7133L11.0578 11.7133ZM6.67316 12.7408C7.68294 12.7408 8.61596 12.4913 9.47223 11.9923C10.3285 11.4933 11.0061 10.8156 11.5051 9.95939C12.0041 9.10315 12.2536 8.17012 12.2536 7.16033C12.2536 6.15053 12.0041 5.2175 11.5051 4.36126C11.0061 3.50501 10.3284 2.8274 9.47223 2.32842C8.616 1.82944 7.68298 1.57995 6.67316 1.57995C5.66334 1.57995 4.73032 1.82944 3.87409 2.32842C3.01787 2.8274 2.34026 3.50501 1.84125 4.36126C1.34225 5.2175 1.09276 6.15053 1.09278 7.16033C1.0928 8.17012 1.3423 9.10315 1.84125 9.95939C2.34021 10.8156 3.01783 11.4933 3.87409 11.9923C4.73036 12.4913 5.66338 12.7408 6.67316 12.7408Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <input
                placeholder="Search topics..."
                value={searchText}
                onChange={ev => {
                  setSearchText(ev.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  // Delay hiding to allow click on suggestion
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                onKeyDown={ev => {
                  if (ev.key === 'Enter') {
                    onSearch();
                    setShowSuggestions(false);
                  }
                  if (ev.key === 'Escape') {
                    setShowSuggestions(false);
                  }
                }}
              />
              <svg
                className="dropdownIcon"
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L5 5L9 1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </label>
            {showSuggestions && (
              <ul className="suggestionsDropdown">
                {autoSuggestArr
                  .filter(
                    suggestion =>
                      !searchText || suggestion.toLowerCase().includes(searchText.toLowerCase())
                  )
                  .map((suggestion, index) => (
                    <li
                      key={index}
                      className="suggestionItem"
                      onMouseDown={() => {
                        setSearchText(suggestion);
                        setShowSuggestions(false);
                        // Trigger scroll to section
                        const sectionId = suggestion.toLowerCase().split(' ').join('-');
                        scrollToSection(sectionId);
                      }}
                    >
                      {suggestion}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>

        <div className="userGuideContent">
          {/* Table of Contents Sidebar */}
          <nav className="tableOfContents">
            <h2 className="tocTitle">Contents</h2>
            <ul className="tocList">
              {userGuideData.sections.map(section => (
                <li key={section.id} className="tocItem">
                  <a
                    href={`#${section.id}`}
                    className={activeSection === section.id ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      scrollToSection(section.id);
                    }}
                  >
                    <span className="tocNumber">{section.number}.</span>
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Main Content */}
          <div className="mainContent">
            {/* Platform Guide Section */}
            <section className="platformGuideSection">
              <h2 className="sectionTitle">{userGuideData.platformGuide.title}</h2>
              <h3 className="subsectionTitle">{userGuideData.platformGuide.subtitle}</h3>
              <div className="contentWrapper">
                {userGuideData.platformGuide.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="contentParagraph">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            {/* Features */}
            <div className="featuresContainer">
              {userGuideData.sections.map(section => (
                <section key={section.id} className="featureSection" id={section.id}>
                  <div className="featureHeader">
                    <div className="featureNumber">{section.number}</div>
                    <h3 className="featureTitle">{section.title}</h3>
                  </div>
                  <div className="featureBody">
                    <div className="featureContent">{renderContent(section.content)}</div>
                    {section.hasVideo && (
                      <div className="videoContainer">
                        <UserGuideVideo src={section.videoSrc} />
                      </div>
                    )}
                  </div>
                </section>
              ))}
            </div>

            {/* Conclusion */}
            <section className="conclusionSection">
              <h3 className="conclusionTitle">{userGuideData.conclusion.title}</h3>
              <p className="contentParagraph">{userGuideData.conclusion.content}</p>
            </section>
          </div>
        </div>
      </div>
    </UserGuideStyle>
  );
}

export default UserGuide;
